import { useMutation, useQuery } from '@apollo/client';
import { useTranslations } from '@vocab/react';
import { useToast } from 'braid-design-system';
import { useCallback } from 'react';

import { useTypedSelector } from 'src/reducers';
import type {
  GetApproachabilityQuery,
  UpdateApproachabilityMutation,
  GetProfileVisibility2Query,
  UpdateProfileVisibility2Mutation,
  GetProfileVisibilityOptionsQuery,
} from 'src/types/graphql';
import { useGetLocale } from 'src/utils/useGetLocale/useGetLocale';

import translations from './.vocab';
import {
  getApproachability,
  getProfileVisibility2,
  updateProfileVisibility2Mutation,
  updateApproachabilityMutation,
  getProfileVisibilityOptions,
} from './visibility.graphql';

export const useGetProfileVisibilityOptions = () => {
  const locale = useGetLocale();
  const zone = useTypedSelector((store) => store.page.pageAnalytics.zone);
  const { data, loading, error } = useQuery<GetProfileVisibilityOptionsQuery>(
    getProfileVisibilityOptions,
    {
      variables: {
        locale,
        zone,
      },
    },
  );

  return {
    options: data?.profileVisibilityOptions,
    loading: loading || Boolean(error),
    error,
  };
};

export const useGetProfileVisibility2 = () => {
  const locale = useGetLocale();
  const zone = useTypedSelector((store) => store.page.pageAnalytics.zone);
  const { data, loading, error } = useQuery<GetProfileVisibility2Query>(
    getProfileVisibility2,
    {
      variables: {
        locale,
        zone,
      },
    },
  );

  const profileVisibility2 = data?.viewer?.profileVisibility2;

  return {
    profileVisibility2,
    loading,
    error,
  };
};

export const useUpdateProfileVisibility2 = () => {
  const showToast = useToast();
  const { t } = useTranslations(translations);

  const onCompleted = useCallback(() => {
    showToast({
      message: t('Profile visibility updated'),
      tone: 'positive',
      key: 'profile-visibility',
    });
  }, [showToast, t]);

  const onError = useCallback(() => {
    showToast({
      message: t('Profile visibility could not be updated'),
      tone: 'critical',
      key: 'profile-visibility',
    });
  }, [showToast, t]);

  const [updateProfileVisibility2, { data, loading, error }] =
    useMutation<UpdateProfileVisibility2Mutation>(
      updateProfileVisibility2Mutation,
      {
        onCompleted,
        onError,
      },
    );

  return {
    updateProfileVisibility2,
    data,
    loading,
    error,
  };
};

export const useGetApproachability = () => {
  const { data, loading, error } =
    useQuery<GetApproachabilityQuery>(getApproachability);

  const approachable = data?.viewer?.approachability?.approachable ?? true;

  return {
    approachable,
    loading,
    error,
  };
};

export const useUpdateApproachability = () => {
  const showToast = useToast();
  const { t } = useTranslations(translations);

  const onCompleted = useCallback(() => {
    showToast({
      message: t('Profile visibility updated'),
      tone: 'positive',
      key: 'profile-visibility',
    });
  }, [showToast, t]);

  const onError = useCallback(() => {
    showToast({
      message: t('Profile visibility could not be updated'),
      tone: 'critical',
      key: 'profile-visibility',
    });
  }, [showToast, t]);

  const [updateApproachability, { data, loading, error }] =
    useMutation<UpdateApproachabilityMutation>(updateApproachabilityMutation, {
      onCompleted,
      onError,
    });

  return { updateApproachability, data, loading, error };
};
